<template>
  <div class="mt-4 w-full">
    <Input
      v-model="searchString"
      :value="searchString"
      :placeholder="__('Search')"
      type="text"
      required
      :maxlength="100"
      @input="handleSearchInput"
    />
  </div>

  <div
    class="scrollbar-thin mt-4 h-[60vh] overflow-auto rounded-lg bg-white shadow-lg [scrollbar-gutter:stable]"
  >
    <table class="min-w-full">
      <thead class="text-gray sticky top-0 z-10 mb-4 bg-white shadow-sm">
        <tr>
          <th
            class="border-b border-gray-200 px-6 py-4 text-left font-semibold uppercase"
          >
            {{ __('Name') }}
          </th>
          <th
            class="border-b border-gray-200 px-6 py-4 text-center font-semibold uppercase"
          >
            {{ __('Status') }}
          </th>
          <th
            class="border-b border-gray-200 px-6 py-4 text-left font-semibold uppercase"
          >
            {{ __('Domain') }}
          </th>
          <th
            class="border-b border-gray-200 px-6 py-4 text-left font-semibold uppercase"
          >
            {{ __('Experience') }}
          </th>
          <th
            class="border-b border-gray-200 px-6 py-4 text-center font-semibold uppercase"
          >
            {{ __('Location') }}
          </th>
          <th
            class="border-b border-gray-200 px-6 py-4 text-center font-semibold uppercase"
          >
            {{ __('Actions') }}
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-200 bg-white text-gray-600">
        <tr v-if="isLoadingCandidates" v-for="n in 5" :key="`skeleton-${n}`">
          <td class="px-6 py-4">
            <div class="h-4 animate-pulse rounded bg-gray-200"></div>
          </td>
          <td class="px-6 py-4">
            <div class="h-4 animate-pulse rounded bg-gray-200"></div>
          </td>
          <td class="px-6 py-4">
            <div class="h-4 w-1/2 animate-pulse rounded bg-gray-200"></div>
          </td>
          <td class="px-6 py-4">
            <div class="h-4 w-1/3 animate-pulse rounded bg-gray-200"></div>
          </td>
          <td class="px-6 py-4">
            <div class="h-4 w-1/3 animate-pulse rounded bg-gray-200"></div>
          </td>
          <td class="px-6 py-4">
            <div class="h-4 w-1/3 animate-pulse rounded bg-gray-200"></div>
          </td>
        </tr>

        <tr v-else-if="isPipelineEmpty">
          <td :colspan="7" class="px-6 py-8 text-center text-gray-500">
            <div class="flex h-full w-full items-center justify-center">
              <div
                class="flex w-[500px] flex-col items-center justify-center pt-16"
              >
                <span class="text-2xl font-medium text-zinc-800">{{
                  __('Your Pipeline Awaits!')
                }}</span>
                <span class="mt-4 text-base text-zinc-800">
                  {{
                    __(
                      "It looks like you haven't added any candidates to this pipeline yet.\
                      This is where you'll track and manage potential talent for your future roles. To get started, you can add candidates directly from our"
                    )
                  }}
                  <strong> {{ __('Candidate Search') }} </strong>
                  {{ __('feature, through the ') }}
                  <strong>{{ __('Candidate Dashboard.') }}</strong>
                </span>
                <Button
                  class="hover-lift mt-8 flex w-fit items-center"
                  transition="transition-all"
                  @click.stop="redirectToPage('search')"
                >
                  <span class="text-center">
                    {{ __('Add potential Candidates') }}
                  </span>
                </Button>
              </div>
            </div>
          </td>
        </tr>

        <tr
          v-else-if="
            data.pipeline.candidates.data.length === 0 && !isLoadingCandidates
          "
        >
          <td :colspan="7" class="px-6 py-8 text-center text-gray-500">
            {{ __('No results found') }}
          </td>
        </tr>

        <tr
          v-else
          v-for="(candidate, index) in data.pipeline.candidates.data"
          class="transition-all duration-200"
          :class="{
            'hover:bg-gray-50': !selectedCandidates.find(
              (item) => item.id === candidate.id
            ),
            'bg-gray-100': selectedCandidates.find(
              (item) => item.id === candidate.id
            ),
            'hover:bg-gray-200': selectedCandidates.find(
              (item) => item.id === candidate.id
            )
          }"
          :key="candidate.id"
        >
          <td class="border-b border-gray-200 px-6 py-4">
            <div
              class="flex h-full w-full items-center justify-start gap-4 gap-x-4"
            >
              <input
                type="checkbox"
                class="size-5 rounded-md border-gray-300 text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                :checked="
                  selectedCandidates.find((item) => item.id === candidate.id)
                "
                @change="toggle(candidate.id)"
              />

              <img
                class="block h-11 w-11 flex-shrink-0 rounded-full ring-2 ring-white"
                :src="candidate.profile_picture_url"
                alt="User Avatar"
                onerror="this.src='https://placehold.co/44x44/cccccc/ffffff?text=User'"
              />
              <span
                class="whitespace-nowrap text-center font-semibold text-zinc-800"
                >{{ candidate.full_name }}</span
              >
            </div>
          </td>

          <td class="border-b border-gray-200 px-6 py-4">
            <FadeTransition>
              <div
                class="flex h-full w-full items-center justify-center"
                :key="candidate.jobfit_status?.name"
              >
                <div
                  class="flex items-center justify-center rounded-lg px-3.5 py-0.5"
                >
                  <span
                    class="w-24 overflow-hidden text-ellipsis whitespace-nowrap text-center text-sm font-medium"
                  >
                    {{ candidate.jobfit_status?.name ?? 'N/A' }}
                  </span>
                </div>
              </div>
            </FadeTransition>
          </td>

          <td class="border-b border-gray-200 px-6 py-4">
            <div class="flex h-full w-full items-center justify-start">
              <span
                class="whitespace-nowrap text-left text-base font-medium text-zinc-600"
                >{{
                  candidate.availability?.user?.domain?.slice(-1)[0]?.name ??
                  'N/A'
                }}</span
              >
            </div>
          </td>

          <td class="border-b border-gray-200 px-6 py-4">
            <div class="flex h-full w-full items-center justify-start">
              <span
                class="whitespace-nowrap text-center text-base font-medium text-zinc-600"
                >{{
                  candidate.experience
                    ? candidate.experience + ' ' + __('years')
                    : 'N/A'
                }}
              </span>
            </div>
          </td>

          <td class="border-b border-gray-200 px-6 py-4">
            <div class="flex h-full w-full items-center justify-center">
              <span
                class="whitespace-nowrap text-center text-base font-medium text-zinc-600"
                >{{
                  candidate.availability?.user?.location.length !== 0
                    ? candidate.availability?.user?.location[0]?.city?.name +
                      ', ' +
                      candidate.availability?.user?.location[0]?.country?.name
                    : 'N/A'
                }}</span
              >
            </div>
          </td>

          <td class="relative border-b border-gray-200 px-6 py-4">
            <div class="flex flex-row items-center justify-center gap-2">
              <CandidateActionsPopup
                :candidateId="candidate.id"
                :options="candidate.jobfit_status_options"
                :job-options="candidate.job_options"
                :status="false"
                :is-loading-status="isLoadingStatus"
                :is-loading-job="isLoadingJob"
                @inviteToJob="handleInviteToJob"
                @changeStatus="handleCandidateChangeStatus"
                @viewNotes="handleViewNotes"
                @goToResume="handleCandidateGoToResume"
                :isOpen="openPopupMenuId === candidate.id"
                @toggle="
                  openPopupMenuId =
                    openPopupMenuId === candidate.id ? false : candidate.id
                "
                @delete="handlePipelineCandidateDelete"
                :position-from-end="
                  data.pipeline.candidates.data.length - index
                "
              >
              </CandidateActionsPopup>
              <button
                class="cursor-pointer"
                @click.stop="openNotesModalId = candidate.id"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                >
                  <mask
                    id="mask0_5952_10855"
                    style="mask-type: alpha"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="24"
                    height="24"
                  >
                    <rect x="0.5" width="24" height="24" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_5952_10855)">
                    <path
                      d="M11.5 18H13.5V15H16.5V13H13.5V10H11.5V13H8.5V15H11.5V18ZM6.5 22C5.95 22 5.47917 21.8042 5.0875 21.4125C4.69583 21.0208 4.5 20.55 4.5 20V4C4.5 3.45 4.69583 2.97917 5.0875 2.5875C5.47917 2.19583 5.95 2 6.5 2H14.5L20.5 8V20C20.5 20.55 20.3042 21.0208 19.9125 21.4125C19.5208 21.8042 19.05 22 18.5 22H6.5ZM13.5 9H18.5L13.5 4V9Z"
                      fill="#27272A"
                    />
                  </g>
                </svg>
              </button>
            </div>
          </td>
        </tr>
        <FadeTransition>
          <tr
            v-if="
              data.pipeline.candidates.current_page !==
                data.pipeline.candidates.last_page && !isLoadingCandidates
            "
          >
            <td :colspan="6" class="p-4 text-center">
              <button
                :disabled="isLoadingShowMore"
                class="hover-lift-no-shadow font-medium text-primary"
                :class="{ 'opacity-50': isLoadingShowMore }"
              >
                <div
                  class="flex items-center justify-center gap-2"
                  @click="
                    this.$emit('loadCandidates', {
                      pipelineId: data.pipeline.id,
                      page: data.pipeline.candidates.current_page + 1,
                      reset: false,
                      search: searchString
                    })
                  "
                >
                  <span>{{ __('Show More') }}</span>
                  <Icon
                    v-if="isLoadingShowMore"
                    class="animate-spin text-lg"
                    name="progress_activity"
                  />
                </div>
              </button>
            </td>
          </tr>
        </FadeTransition>
        <!-- <tr className="pointer-events-none">
            <td colSpan="6" class="h-40 border-0 bg-transparent"></td>
          </tr> -->
      </tbody>
    </table>
  </div>
  <div class="relative mb-4 h-28 pt-4">
    <FadeTransition>
      <div
        v-show="selectedCandidates.length"
        class="mt-2 flex w-full scroll-mb-24 flex-col items-center justify-between gap-4 rounded-lg bg-white px-6 py-4 shadow-lg sm:flex-row sm:gap-0"
      >
        <div
          class="flex w-full items-center justify-between gap-x-2 sm:justify-start"
        >
          <span class="px-4 py-2 text-zinc-600">
            {{ selectedCandidates.length }} {{ __('selected') }}
          </span>

          <button
            class="hover-lift-no-shadow flex items-center justify-center gap-2 px-4 py-2"
            @click="
              selectedCandidates.length == data.pipeline.candidates.data.length
                ? (selectedCandidates = [])
                : (selectedCandidates = data.pipeline.candidates.data.map(
                    (c) => ({ id: c.id, updated_at: c.updated_at })
                  ))
            "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              class="fill-primary"
            >
              <mask
                id="mask0_5988_17"
                style="mask-type: alpha"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="16"
                height="16"
              >
                <rect width="16" height="16" fill="#D9D9D9" />
              </mask>
              <g mask="url(#mask0_5988_17)">
                <path
                  d="M2.66683 14C2.30016 14 1.98627 13.8694 1.72516 13.6083C1.46405 13.3472 1.3335 13.0333 1.3335 12.6667V3.33333C1.3335 2.96667 1.46405 2.65278 1.72516 2.39167C1.98627 2.13056 2.30016 2 2.66683 2H13.3335C13.7002 2 14.0141 2.13056 14.2752 2.39167C14.5363 2.65278 14.6668 2.96667 14.6668 3.33333V12.6667C14.6668 13.0333 14.5363 13.3472 14.2752 13.6083C14.0141 13.8694 13.7002 14 13.3335 14H2.66683ZM3.3335 11.3333H6.66683V10H3.3335V11.3333ZM9.70016 10L13.0002 6.7L12.0502 5.75L9.70016 8.11667L8.75016 7.16667L7.81683 8.11667L9.70016 10ZM3.3335 8.66667H6.66683V7.33333H3.3335V8.66667ZM3.3335 6H6.66683V4.66667H3.3335V6Z"
                />
              </g>
            </svg>
            <span class="text-sm font-medium text-primary">
              {{
                selectedCandidates.length ===
                data.pipeline.candidates.data.length
                  ? __('Deselect all')
                  : __('Select All')
              }}
            </span>
          </button>
        </div>
        <div
          class="flex w-full items-center justify-between gap-x-2 sm:justify-end"
        >
          <button
            class="hover-lift-no-shadow relative flex items-center justify-center gap-2 py-2 pr-4"
            variant="ghost-no-padding"
            @click.stop="
              isChangeStatusBulkPopupOpen = !isChangeStatusBulkPopupOpen
            "
          >
            <div class="flex items-center justify-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                class="fill-primary"
              >
                <mask
                  id="mask0_6035_882"
                  style="mask-type: alpha"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="14"
                  height="14"
                >
                  <rect width="14" height="14" />
                </mask>
                <g mask="url(#mask0_6035_882)">
                  <path
                    d="M10.9188 5.38356L8.59333 3.08219L9.35936 2.31507C9.56911 2.10502 9.82673 2 10.1322 2C10.4377 2 10.6954 2.10502 10.9051 2.31507L11.6711 3.08219C11.8809 3.29224 11.9903 3.54566 11.9994 3.84247C12.0085 4.13927 11.9082 4.39269 11.6985 4.60274L10.9188 5.38356ZM2.54716 12C2.39213 12 2.26218 11.9475 2.15731 11.8425C2.05244 11.7374 2 11.6073 2 11.4521V9.90411C2 9.83105 2.01368 9.76027 2.04104 9.69178C2.0684 9.62329 2.10943 9.56164 2.16415 9.50685L7.79994 3.86301L10.1254 6.19178L4.4896 11.8356C4.43488 11.8904 4.37333 11.9315 4.30493 11.9589C4.23653 11.9863 4.16586 12 4.0929 12H2.54716Z"
                    fill="#D71F70"
                  />
                </g>
              </svg>
              <span class="text-sm font-medium text-primary">
                {{ __('Change Status') }}
              </span>
            </div>
            <CandidateChangeStatusBulkPopup
              :is-loading="isLoadingStatus"
              :candidateIds="
                selectedCandidates.map((candidate) => candidate.id)
              "
              :options="options"
              :is-open="isChangeStatusBulkPopupOpen"
              @changeStatus="handleCandidateChangeBulkStatus"
            >
            </CandidateChangeStatusBulkPopup>
          </button>
          <button
            class="hover-lift-no-shadow relative mr-0 flex min-w-fit flex-shrink-0 items-center justify-center rounded-md border border-primary px-2 py-2 sm:mr-4"
            @click.stop="isMoveToPipelinePopupOpen = !isMoveToPipelinePopupOpen"
          >
            <div
              class="flex min-w-max flex-shrink-0 items-center justify-start gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-4 text-primary"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15.75 19.5 8.25 12l7.5-7.5"
                />
              </svg>

              <span class="text-sm font-medium text-primary">
                {{ __('Move to Pipeline') }}
              </span>
            </div>
            <CandidateMoveToPipelinePopup
              :is-loading="isLoadingMoveToPipeline"
              :candidateIds="
                selectedCandidates.map((candidate) => candidate.id)
              "
              :options="data.options.available_pipelines"
              :is-open="isMoveToPipelinePopupOpen"
              @moveToPipeline="handleCandidateMoveToPipeline"
            >
            </CandidateMoveToPipelinePopup>
          </button>
        </div>
      </div>
    </FadeTransition>
  </div>
  <PipelineCandidateNotesModal
    v-if="openNotesModalId"
    @cancel="openNotesModalId = null"
    :candidateId="openNotesModalId"
    :data="
      data.pipeline.candidates.data.find(
        (candidate) => candidate.id === openNotesModalId
      ).notes
    "
    :recruiterAvatarUrl="data.pipeline.current_recruiter.profile_picture_url"
    @addNote="handleCandidateAddNote"
  >
  </PipelineCandidateNotesModal>
</template>

<script setup>
  import axios from 'axios';

  import { ref, watch } from 'vue';

  import { nextTick, onMounted } from 'vue';

  import CandidateActionsPopup from './components/CandidateActionsPopup.vue';

  import FadeTransition from '@/components/global/FadeTransition.vue';
  import Input from '@/components/global/Form/Input.vue';
  import Icon from '@/components/global/Icon.vue';
  import { __, scrollToBottom } from '@/helpers';
  import { useToast } from 'vue-toastification';
  import CandidateChangeStatusBulkPopup from './components/CandidateChangeStatusBulkPopup.vue';
  import CandidateMoveToPipelinePopup from './components/CandidateMoveToPipelinePopup.vue';
  import PipelineCandidateNotesModal from './components/PipelineCandidateNotesModal.vue';
  import Button from '@/components/block/button/button.vue';

  const props = defineProps({
    data: {
      type: Object,
      required: true
    },
    options: {
      type: Array,
      required: true
    },
    isLoadingShowMore: {
      type: Boolean,
      default: false
    },
    isLoadingCandidates: {
      type: Boolean,
      default: false
    }
  });
  const openNotesModalId = ref(null);
  const data = ref(props.data || []);
  const isPipelineEmpty = ref(
    props.data.pipeline.candidates.data.length ? false : true
  );

  const toast = useToast();
  const searchString = ref(null);
  const emit = defineEmits([
    'loadCandidates',
    'moveToAnotherPipeline',
    'update:modelValue'
  ]);
  const openPopupMenuId = ref(null);
  const isLoadingMoveToPipeline = ref(false);
  const isMoveToPipelinePopupOpen = ref(false);
  const isChangeStatusBulkPopupOpen = ref(false);
  const isLoadingJob = ref(false);
  const isLoadingCandidates = ref(props.isLoadingCandidates || false);
  watch(
    () => isChangeStatusBulkPopupOpen.value,
    (newValue) => {
      if (newValue) {
        openPopupMenuId.value = null;
        isMoveToPipelinePopupOpen.value = false;
      }
    }
  );

  watch(
    () => isMoveToPipelinePopupOpen.value,
    (newValue) => {
      if (newValue) {
        openPopupMenuId.value = null;
        isChangeStatusBulkPopupOpen.value = false;
      }
    }
  );

  watch(
    () => openPopupMenuId.value,
    async (newValue) => {
      if (newValue) {
        isChangeStatusBulkPopupOpen.value = false;
        isMoveToPipelinePopupOpen.value = false;

        await nextTick();
        scrollToActivePopup();
      }
    }
  );

  const scrollToActivePopup = async () => {
    await nextTick();

    const activePopup = document.querySelector('[data-popup-menu]');

    if (!activePopup) {
      return;
    }

    const scrollContainer =
      document.querySelector('.overflow-auto') || document.documentElement;

    const popupRect = activePopup.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();

    const popupBottom = popupRect.bottom;
    const containerBottom = containerRect.bottom;

    if (popupBottom > containerBottom) {
      const scrollOffset = popupBottom - containerBottom + 20;

      scrollContainer.scrollBy({
        top: scrollOffset,
        behavior: 'smooth'
      });
    }
  };

  onMounted(() => {
    document.addEventListener('click', (e) => {
      const clickedOnMenu =
        e.target.closest('[data-popup-menu]') ||
        e.target.closest('[data-status-menu]') ||
        e.target.closest('[data-move-to-pipeline-menu]');

      if (!clickedOnMenu) {
        openPopupMenuId.value = null;
        isChangeStatusBulkPopupOpen.value = false;
        isMoveToPipelinePopupOpen.value = false;
      }
    });
  });

  const handleCandidateChangeBulkStatus = async (eventData) => {
    isLoadingStatus.value = true;
    try {
      const response = await axios.patch(
        route('redesign.company.pipelines.candidates.change-status-bulk'),
        {
          candidates: selectedCandidates.value,
          status: eventData.status
        }
      );

      const updatedCandidatesMap = new Map(
        response.data.updated_candidates.map((updatedCandidate) => [
          updatedCandidate.id,
          updatedCandidate
        ])
      );

      data.value.pipeline.candidates.data =
        data.value.pipeline.candidates.data.map((candidate) => ({
          ...candidate,
          ...updatedCandidatesMap.get(candidate.id)
        }));

      // Update selectedCandidates with new updated_at values
      selectedCandidates.value = selectedCandidates.value.map((selected) => ({
        id: selected.id,
        updated_at:
          updatedCandidatesMap.get(selected.id)?.updated_at ||
          selected.updated_at
      }));
      toast.success(
        response?.data?.message ||
          __('Candidates status changed to') +
            ` ${response?.data?.updated_candidates?.[0]?.jobfit_status?.name}`
      );
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    } finally {
      isLoadingStatus.value = false;
      isChangeStatusBulkPopupOpen.value = false;
    }
  };
  const isLoadingStatus = ref(false);

  const handleCandidateChangeStatus = async (eventData) => {
    isLoadingStatus.value = true;

    const candidate = data.value.pipeline.candidates.data.find(
      (item) => item.id === eventData.candidateId
    );

    try {
      const response = await axios.patch(
        route(
          'redesign.company.pipelines.candidates.change-status',
          eventData.candidateId
        ),
        {
          status: eventData.status,
          updated_at: candidate.updated_at
        }
      );

      if (candidate && response.data.jobfit_status) {
        candidate.jobfit_status = response.data.jobfit_status;
        candidate.jobfit_status_options = response.data.jobfit_status_options;
        candidate.updated_at = response.data.updated_at;

        const updated_candidate = selectedCandidates.value.find(
          (selectedCandidate) => selectedCandidate.id === candidate.id
        );

        if (updated_candidate) {
          updated_candidate.updated_at = candidate.updated_at;
        }

        toast.success(
          response?.data?.message ||
            __('Candidate status changed to') +
              ` ${response?.data?.jobfit_status?.name}`
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    } finally {
      isLoadingStatus.value = false;
      openPopupMenuId.value = null;
    }
  };
  function handleCandidateGoToResume(candidateId) {
    window.location.href = data.value.pipeline.candidates.data.find(
      (item) => item.id === candidateId
    ).cv_url;
  }

  const handlePipelineCandidateDelete = async (eventData) => {
    isLoadingCandidates.value = true;

    const candidate = data.value.pipeline.candidates.data.find(
      (item) => item.id === eventData.candidateId
    );

    try {
      const response = await axios.delete(
        route('redesign.company.pipelines.candidates.delete', {
          updated_at: candidate.updated_at,
          candidateId: eventData.candidateId
        })
      );

      emit('loadCandidates', {
        pipelineId: data.value.pipeline.id,
        page: 1,
        reset: true,
        search_query: searchString.value
      });

      toast.success(
        response?.data?.message || 'Candidate deleted successfully'
      );
      selectedCandidates.value = selectedCandidates.value.filter(
        (c) => c.id !== eventData.candidateId
      );
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || __('Something went wrong'));
    } finally {
      openPopupMenuId.value = null;
      isLoadingCandidates.value = false;
    }
  };

  const handleCandidateAddNote = async (noteData) => {
    try {
      const response = await axios.post(
        route(
          'redesign.company.pipelines.candidates.add-note',
          noteData.candidate_id
        ),
        {
          note_text: noteData.note_text
        }
      );

      toast.success(response?.data?.message || __('Note added successfully'));
      const candidate = data.value.pipeline.candidates.data.find(
        (item) => item.id === noteData.candidate_id
      );
      if (candidate) {
        candidate.notes.push(response.data.note);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || __('Something went wrong'));
    }
  };

  function handleViewNotes(candidateId) {
    openNotesModalId.value = candidateId;
  }

  const selectedCandidates = ref([]);

  const toggle = (candidateId) => {
    const candidate = data.value.pipeline.candidates.data.find(
      (item) => item.id === candidateId
    );

    if (selectedCandidates.value.find((item) => item.id === candidateId)) {
      selectedCandidates.value = selectedCandidates.value.filter(
        (selectedCandidate) => selectedCandidate.id !== candidateId
      );
    } else {
      selectedCandidates.value.push({
        id: candidateId,
        updated_at: candidate.updated_at
      });
      if (selectedCandidates.value.length > 0) scrollToBottom();
    }
  };

  let searchTimeout = null;
  function handleSearchInput() {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      emit('loadCandidates', {
        pipelineId: data.value.pipeline.id,
        page: 1,
        reset: true,
        search_query: searchString.value
      });
    }, 300);
  }

  const handleCandidateMoveToPipeline = async (eventData) => {
    isLoadingCandidates.value = true;
    isLoadingMoveToPipeline.value = true;

    try {
      const response = await axios.post(
        route('redesign.company.pipelines.candidates.move', {
          sourcePipeline: data.value.pipeline.id,
          destinationPipeline: eventData.destinationPipelineId
        }),
        {
          candidates: eventData.candidateIds
        }
      );

      emit('loadCandidates', {
        pipelineId: data.value.pipeline.id,
        page: 1,
        reset: true,
        search_query: searchString.value
      });

      isPipelineEmpty.value = true;
      toast.success(
        response?.data?.message || __('Candidates moved successfully')
      );
      selectedCandidates.value = [];
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    } finally {
      setTimeout(() => {
        isLoadingCandidates.value = false;
        isLoadingMoveToPipeline.value = false;
        isMoveToPipelinePopupOpen.value = false;
      }, 500);
    }
  };

  const handleInviteToJob = async (eventData) => {
    isLoadingJob.value = true;

    const candidate = data.value.pipeline.candidates.data.find(
      (item) => item.id === eventData.candidateId
    );

    try {
      const response = await axios.post(
        route('redesign.matching.inviteToJob'),
        {
          job_id: eventData.jobId,
          user_id: candidate.user_id
        }
      );

      toast.success(response?.data?.message || __('Invite sent successfully'));

      candidate.job_options = candidate.job_options.filter(
        (job) => job.id !== eventData.jobId
      );

      isLoadingJob.value = false;
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    } finally {
      isLoadingJob.value = false;
    }
  };

  const redirectToPage = (name, params) => {
    window.location.href = route(name, params);
  };
</script>
